import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { GradeCatalog, GradesCatalogUi } from '../models/grade-catalog.model';
import { CatalogScope, CatalogFilterMode } from '../models/catalog-scope.enum';
import { GradesService } from '../shared/services/grades-catalog.service';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { getControlErrors, isControlInvalid } from 'src/app/shared/services/validation-helpers';
import { catchError, map } from 'rxjs/operators';
import { forkJoin, Observable, Subscription, lastValueFrom } from 'rxjs';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { TemperatureDerationService } from '../shared/services/temperature-deration.service';
import { TemperatureDerationCatalog } from '../models/temperature-deration-catalog.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { saveAs } from 'file-saver-es';
import ExcelJS from "exceljs";
import { Store } from '@ngneat/elf';
import { UserRoles } from 'src/app/core/components/user-admin-page/user-model';
import { Utilities } from 'src/app/core/services/utilities';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { GetSolidsDensityFromLbmFt, GetValueFromPsi } from 'src/app/perivis/shared/helpers/units.helper';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { filterGradesByTypeAndName, normalizeGradeDiscriminator } from '../shared/helpers/grade-discriminator.helper';
import { getDuplicateCatalogColors } from '../shared/helpers/catalog-duplicate-highlight.helper';

@Component({
  selector: 'app-grades-catalog',
  templateUrl: './grades-catalog.component.html',
  providers: [DialogService, ConfirmationService],
  styles: `
    .catalog-row-disabled > td:not(:first-child) {
      opacity: 0.6;
    }
  `,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GradesCatalogComponent implements OnInit, OnDestroy {
  public control(group: FormGroup, name: string): UntypedFormControl {
    return group.get(name) as UntypedFormControl;
  }
  
  private _grades: GradeCatalog[];
  private _filteredGrades: GradeCatalog[] = [];
  private _temperatureDerations: TemperatureDerationCatalog[];
  private _subscriptions: Subscription = new Subscription();
  private _defaultTempDeration: TemperatureDerationCatalog;

  public strengthCalculationOptions: { label: string, value: string }[];
  public gradeTypeOptions: { label: string, value: string }[] = [
    { label: 'Tubular Pipe', value: 'TubularPipe' },
    { label: 'Drill Pipe', value: 'DrillPipe' },
    { label: 'HWDP', value: 'Hwdp' },
    { label: 'Collar', value: 'Collar' }
  ];
  public columnDefs: any[];
  public isLoading = signal<boolean>(true);
  public currentFilter = '';
  public selectedType = 'TubularPipe';
  public gradesForm: FormGroup;
  public tableHeight = signal<string>('600px');
  public formIsValid = signal<boolean>(true);
  public paginatorFirst = signal<number>(0);
  public pressureUnit: string;
  public thermalCondUnit: string;
  public solidsDensityUnit: string;
  public thermalExpCoeffUnit: string;
  public userRoles = signal<UserRoles | undefined>(undefined);
  public specificHeatCapacityUnit: string;
  public temperatureDerations: { label: string, value: TemperatureDerationCatalog }[] = [];
  public validationRules = {
    minimumApiYieldStrength: { min: 0.001, max: 400000 },
    youngsModulus: { min: 0.001, max: 250000000 },
    poissonsRatio: { min: 0, max: 0.5 },
    thermalExpansionCoefficient: { min: 0, max: 100 },
    ultimateTensileStrength: { min: 0.001, max: 500000 },
    density: { min: 0.001, max: 1000 },
    specificHeatCapacity: { min: 0.001, max: 5 },
    thermalConductivity: { min: 0.001, max: 2000 }
  };
  public apiGrades = ['B', 'C-75', 'C-90', 'C-95', 'H-40', 'J-55', 'K-55', 'L-80', 'M-65', 'N-80', 'P-105', 'P-110', 'T-95', 'Q-125', 'V-150', 'X-42', 'X-46', 'X-52', 'X-56', 'X-60', 'X-80'];
  public rows = 15;
  public duplicateRowColors = signal<ReadonlyMap<string, string>>(new Map());

  @ViewChild("catalogTable") catalogTable;

  // Scope filtering
  public CatalogScope = CatalogScope;
  public selectedScopeFilter: CatalogFilterMode = CatalogFilterMode.AllItems;

  get gradesCatalog(): FormArray {
    return this.gradesForm.get("grades") as FormArray;
  }

  // Validation delegates
  public isControlInvalid: Function = isControlInvalid;
  public getControlErrors: Function = getControlErrors;

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.gradesCatalogStore = this._storeService.createStore(this.componentId, new GradesCatalogUi);
    this.selectedType = this.gradesCatalogStore.state.selectedType || 'TubularPipe';
  }
  get componentId(): string {
    return this._componentId;
  }
  public gradesCatalogStore: Store;

  constructor(
    private _messenger: MediatorService,
    private _confirmationService: ConfirmationService,
    private _gradesCatalogService: GradesService,
    private _formBuilder: FormBuilder,
    private _temperatureDerationService: TemperatureDerationService,
    private _storeService: StoreService,
    private _signalRService: SignalRService,
    private _toaster: AppNotificationService
  ) {
    this.strengthCalculationOptions = [
      { label: 'API', value: 'Api' },
      { label: 'Non-API', value: 'NonApi' }
    ];

    this.gradesForm = this._formBuilder.group({
      grades: this._formBuilder.array([]),
    });

    this._subscriptions.add(
      this.gradesCatalog.valueChanges.subscribe(() => this.refreshDuplicateRowColors())
    );
  }

  async ngOnInit(): Promise<void> {
    this.userRoles.set(await this._storeService.get<UserRoles>(StorageKeys.ROLES) ?? undefined);
    const uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.pressureUnit = uu.stressPressure;
    this.thermalCondUnit = uu.thermalConductivity;
    this.solidsDensityUnit = uu.solidsDensity;
    this.thermalExpCoeffUnit = unitsLib[uu.coefficientOfThermalExpansion].symbol;
    this.specificHeatCapacityUnit = unitsLib[uu.specificHeatCapacity].symbol;
    this.validationRules = this.getValidationRules();

    this.columnDefs = [
      { field: 'name', header: 'Name' },
      { field: 'minimumApiYieldStrength', header: `API Min Yield (${this.pressureUnit})` },
      { field: 'youngsModulus', header: `Young's Modulus (${this.pressureUnit})` },
      { field: 'poissonsRatio', header: 'Poisson\'s Ratio' },
      { field: 'thermalExpansionCoefficient', header: `Thermal Exp Coeff (${this.thermalExpCoeffUnit})` },
      { field: 'ultimateTensileStrength', header: `UTS (${this.pressureUnit})` },
      { field: 'density', header: `Density (${this.solidsDensityUnit})` },
      { field: 'specificHeatCapacity', header: `Specific Heat Capacity (${this.specificHeatCapacityUnit})` },
      { field: 'thermalConductivity', header: `Thermal Conductivity (${this.thermalCondUnit})` },
      { field: 'temperatureDeration', header: 'Temperature Deration' },
      { field: 'strengthCalculationOption', header: 'Strength Calculation' }
    ];

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.getGradesData(false);

    // Track FormArray validity with signal for OnPush change detection
    this._subscriptions.add(
      this.gradesCatalog.statusChanges.subscribe(() => {
        this.formIsValid.set(this.gradesCatalog.valid);
      })
    );

    addEventListener("keydown", (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    });
  }

  private signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_TEMP_DERATION_CATALOG) {
      this._grades = [];
      this.getGradesData(true);
    }
  }

  private getGradesData(isCalledFromSignalR: boolean) {
    this.isLoading.set(true);
    const sources: Observable<any>[] = [
      this._gradesCatalogService.getGrades(this.selectedScopeFilter) as Observable<GradeCatalog[]>,
      this._temperatureDerationService.getTemperatureDerations() as Observable<TemperatureDerationCatalog[]>
    ];

    forkJoin(sources).pipe(
      map(([grades, temperatureDerations]) => {
        this._grades = (grades ?? []).map(grade => ({
          ...grade,
          discriminator: this.normalizeDiscriminator(grade?.discriminator)
        }));
        this._temperatureDerations = temperatureDerations;
        this._defaultTempDeration = this._temperatureDerations.find(x => x.name == 'Default') ?? this._temperatureDerations[0];
        this.temperatureDerations = temperatureDerations.map(g => ({ label: g.name, value: g }));

        if (!isCalledFromSignalR) {
          this.filterGrades(this.currentFilter);
        }

        this.isLoading.set(false);
      }),
      catchError(err => {
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  public onScopeFilterChange(): void {
    this.getGradesData(false);
  }

  public onTypeFilterChange(discriminator: string): void {
    this.selectedType = this.normalizeDiscriminator(discriminator ?? 'TubularPipe');
    this.filterGrades(this.currentFilter);
  }

  public onPageChange(event: { first?: number }): void {
    this.paginatorFirst.set(event.first ?? 0);
  }

  public onAddGrade(idx?: number): void {
    this.gradesCatalog.insert(idx || 0, this.newGrade());
    this.paginatorFirst.set(0);
    this.catalogTable.first = 0;
    this.catalogTable?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  public onDeleteGrade(idx: number): void {
    const gradeId = this.gradesCatalog.controls[idx].get("id").value;
    const gradeName = this.gradesCatalog.controls[idx].get("name").value;

    if (gradeId.length < 36) {
      this.gradesCatalog.removeAt(idx);
      this._grades = (this._grades ?? []).filter(g => g.id !== gradeId);
      this._filteredGrades = this._filteredGrades.filter(g => g.id !== gradeId);
    } else {
      this._confirmationService.confirm({
        message: `Are you sure that you want to delete the selected grade ${gradeName}?`,
        accept: async () => {
          if (gradeId.length > 24) {
            await lastValueFrom(this._gradesCatalogService.deleteGrade(gradeId));
            this._messenger.publish(new DataSavedMessage('Grades Catalog'));
          }

          this._grades = (this._grades ?? []).filter(g => g.id !== gradeId);
          this._filteredGrades = this._filteredGrades.filter(g => g.id !== gradeId);
          this.gradesCatalog.removeAt(idx);
        }
      });
    }
  }

  public deleteGradeCheck(gradeFg: FormGroup): boolean {
    const isAdmin = this.userRoles()?.catalogAdmin;
    const scope = gradeFg.get('scope').value;
    const isLocked = gradeFg.get('isLocked').value;

    return isLocked || (!isAdmin && scope !== CatalogScope.User);
  }

  public showJson(data: any) {
    return JSON.stringify(data, null, '\t');
  }

  public setHoverTitle(event: Event, title: string): void {
    const target = event.currentTarget as HTMLElement | null;
    if (target) {
      target.title = title;
    }
  }

  public getScopeLabel(scope: CatalogScope): string {
    return scope === CatalogScope.Global ? 'Global' : 'User';
  }

  public getScopeBadgeClass(scope: CatalogScope): string {
    return scope === CatalogScope.Global ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  }

  public normalizeDiscriminator(discriminator: string | null | undefined): string {
    return normalizeGradeDiscriminator(discriminator);
  }

  public getDiscriminatorLabel(discriminator: string | null | undefined): string {
    const normalized = this.normalizeDiscriminator(discriminator);
    return this.gradeTypeOptions.find(x => x.value === normalized)?.label ?? normalized;
  }

  public async toggleScope(formGroup: FormGroup): Promise<void> {
    this.isLoading.set(true);
    const currentScope = formGroup.get('scope').value;
    const newScope = currentScope === CatalogScope.User ? CatalogScope.Global : CatalogScope.User;

    const gradeData = formGroup.value;
    gradeData.scope = newScope;

    await lastValueFrom(this._gradesCatalogService.updateGrade(new GradeCatalog(gradeData)));

    // Only update the form if save succeeds
    formGroup.get('scope').patchValue(newScope, { emitEvent: false });

    // Update the local cache
    const index = this._grades.findIndex(x => x.id === gradeData.id);
    if (index > -1) {
      this._grades[index].scope = newScope;
    }

    this._messenger.publish(new DataSavedMessage('Grades Catalog'));
    this.isLoading.set(false);

  }

  public getPlaceholderText(data: any) {
    if (data == null) {
      return 'Select';
    }

    const tempDerationExists = this._temperatureDerations.findIndex(x =>
      x.name == data?.name &&
      JSON.stringify(data.temperatureDerationProfile) == JSON.stringify(x.temperatureDerationProfile)
    ) >= 0;

    return tempDerationExists ? data.name : data.name + ' (local only)';
  }

  private async handleSaveGradeRow(v: any, gradeRecord: FormGroup): Promise<void> {
    const gradeName = v.name;
    let existingGrade = this._grades.find(x =>
      x.name === gradeName &&
      this.normalizeDiscriminator(x.discriminator) === this.normalizeDiscriminator(v.discriminator) &&
      this.approximatelyEqual(x.minimumApiYieldStrength, v.minimumApiYieldStrength) &&
      this.approximatelyEqual(x.youngsModulus, v.youngsModulus) &&
      this.approximatelyEqual(x.poissonsRatio, v.poissonsRatio) &&
      this.approximatelyEqual(x.thermalExpansionCoefficient, v.thermalExpansionCoefficient) &&
      this.approximatelyEqual(x.ultimateTensileStrength, v.ultimateTensileStrength) &&
      this.approximatelyEqual(x.density, v.density) &&
      this.approximatelyEqual(x.specificHeatCapacity, v.specificHeatCapacity) &&
      this.approximatelyEqual(x.thermalConductivity, v.thermalConductivity) &&
      (x.temperatureDeration?.id === v.temperatureDeration?.id) &&
      x.strengthCalculationOption === v.strengthCalculationOption &&
      x.scope === v.scope
    );

    if (existingGrade && gradeRecord.controls.temperatureDeration.pristine) {
      return;
    }

    if (gradeRecord.valid && !this.isLoading()) {
      existingGrade = this._grades.find(x => x.name == gradeName && x.id != v.id && x.discriminator === v.discriminator);
      if (existingGrade) {
        this._toaster.showError(`Grade with name "${gradeName}" already exists.`);
        return;
      }
      if (v.id.length == 24) { // it's only on the client so save to db as a new record
        this.isLoading.set(true);
        const newEntityId = await lastValueFrom(this._gradesCatalogService.addGrade(new GradeCatalog(v)));
        gradeRecord.get("id").patchValue(newEntityId, { emitEvent: false });
        this._grades.unshift(gradeRecord.value);
        this.isLoading.set(false);
      } else {
        await lastValueFrom(this._gradesCatalogService.updateGrade(new GradeCatalog(v)));
        const index = this._grades.findIndex(x => x.id === v.id);
        if (index > -1) {
          this._grades[index] = gradeRecord.value;
        }
      }
      this._messenger.publish(new DataSavedMessage('Grades Catalog'));
    }
  }

  approximatelyEqual(a, b) {
    if (a === b) return true;
    if (typeof a !== 'number' || typeof b !== 'number') return false;
    return Math.abs(a - b) < Number.EPSILON * Math.max(Math.abs(a), Math.abs(b));
  }

  private newGrade(): FormGroup {
    const gradeFg = new FormGroup({
      id: new FormControl(Utilities.generateFakeGuid()), // Setting a unique id here so that prime-ng p-table has a data key for the expand and collapse functionality.
      name: new FormControl("", { updateOn: 'blur', validators: [Validators.required] }),
      minimumApiYieldStrength: new FormControl("", { updateOn: 'blur', validators: [Validators.required] }),
      youngsModulus: new FormControl(GetValueFromPsi(30000000, this.pressureUnit), { updateOn: 'blur', validators: [Validators.required] }),
      poissonsRatio: new FormControl(0.3, { updateOn: 'blur', validators: [Validators.min(0), Validators.max(0.5), Validators.required] }),
      thermalExpansionCoefficient: new FormControl(this.thermalExpCoeffUnit == '1E-6/°F' ? 6.9 : 12.42, { updateOn: 'blur', validators: [Validators.required] }),

      ultimateTensileStrength: new FormControl("", { updateOn: 'blur', validators: [Validators.required] }),
      density: new FormControl(GetSolidsDensityFromLbmFt(490, this.solidsDensityUnit), { updateOn: 'blur', validators: [Validators.required] }),
      specificHeatCapacity: new FormControl(this.specificHeatCapacityUnit == 'BTU/lbm-°F' ? 0.11 : 460.54, { updateOn: 'blur', validators: [Validators.required] }),
      thermalConductivity: new FormControl(this.thermalCondUnit == 'BTU/h·ft·°F' ? 26.2 : 45.35, { updateOn: 'blur', validators: [Validators.required] }),
      discriminator: new FormControl(this.normalizeDiscriminator(this.selectedType), [Validators.required]),
      temperatureDeration: new FormControl(this._defaultTempDeration, [Validators.required]),
      strengthCalculationOption: new FormControl("Api"),
      isLocked: new FormControl(false),
      metadata: new FormControl(null),
      scope: new FormControl(CatalogScope.User)
    });

    this._subscriptions.add(gradeFg.valueChanges.subscribe((v) => this.handleSaveGradeRow(v, gradeFg)));

    gradeFg.markAllAsTouched();

    return gradeFg;
  }

  private getValidationRules() {
    const stress = (value: number): number => GetValueFromPsi(value, this.pressureUnit);
    const density = (value: number): number => GetSolidsDensityFromLbmFt(value, this.solidsDensityUnit);
    const thermalExpansion = (value: number): number => this.thermalExpCoeffUnit === '1E-6/°C' ? value * 1.8 : value;
    const specificHeat = (value: number): number => this.specificHeatCapacityUnit === 'J/kg-K' ? value * 4186.8 : value;
    const thermalConductivity = (value: number): number => this.thermalCondUnit === 'W/m·K' ? value * 1.730735 : value;

    return {
      minimumApiYieldStrength: { min: stress(0.001), max: stress(400000) },
      youngsModulus: { min: stress(0.001), max: stress(250000000) },
      poissonsRatio: { min: 0, max: 0.5 },
      thermalExpansionCoefficient: { min: thermalExpansion(0), max: thermalExpansion(100) },
      ultimateTensileStrength: { min: stress(0.001), max: stress(500000) },
      density: { min: density(0.001), max: density(1000) },
      specificHeatCapacity: { min: specificHeat(0.001), max: specificHeat(5) },
      thermalConductivity: { min: thermalConductivity(0.001), max: thermalConductivity(2000) }
    };
  }

  private populateFormData(grades: GradeCatalog[] = []): void {
    this.gradesCatalog.clear();
    grades.forEach((data, idx) => {
      const gradeFg = this.newGrade();
      this.gradesCatalog.push(gradeFg);
      const expandedData = {
        ...data,
        discriminator: this.normalizeDiscriminator(data?.discriminator)
      };
      this.gradesCatalog.controls[idx].setValue(expandedData, { emitEvent: false });
      this.syncGradeRowState(this.gradesCatalog.controls[idx] as FormGroup);
    });
    this.refreshDuplicateRowColors();
  }

  private refreshDuplicateRowColors(): void {
    if (!this.userRoles()?.catalogAdmin) {
      this.duplicateRowColors.set(new Map());
      return;
    }

    this.duplicateRowColors.set(getDuplicateCatalogColors(this.gradesCatalog.getRawValue(), grade => ({
      name: grade.name ?? null,
      discriminator: this.normalizeDiscriminator(grade.discriminator),
      minimumApiYieldStrength: grade.minimumApiYieldStrength ?? null,
      youngsModulus: grade.youngsModulus ?? null,
      poissonsRatio: grade.poissonsRatio ?? null,
      thermalExpansionCoefficient: grade.thermalExpansionCoefficient ?? null,
      ultimateTensileStrength: grade.ultimateTensileStrength ?? null,
      density: grade.density ?? null,
      specificHeatCapacity: grade.specificHeatCapacity ?? null,
      thermalConductivity: grade.thermalConductivity ?? null,
      temperatureDerationId: grade.temperatureDeration?.id ?? null,
      strengthCalculationOption: grade.strengthCalculationOption ?? null,
      scope: grade.scope ?? null
    })));
  }

  private syncGradeRowState(gradeFg: FormGroup): void {
    if (this.updateFormDisabledState(gradeFg)) {
      gradeFg.disable({ emitEvent: false });
      return;
    }

    gradeFg.enable({ emitEvent: false });
  }

  public updateFormDisabledState(gradeFg: FormGroup): boolean {
    return this.deleteGradeCheck(gradeFg) || this.userRoles()?.readOnly || false;
  }

  public async exportExcel(): Promise<void> {
    const mappedData = this.gradesCatalog.controls.map(td => {
      const objWithHeaderKeys = {};
      this.columnDefs.forEach(cd => {
        const objHeaderKey = cd.header;
        const name = cd.field != 'temperatureDeration' ? td.value[cd.field] : td.value[cd.field].name;
        objWithHeaderKeys[objHeaderKey.replace(/["']/g, '"')] = name;
      });
      return objWithHeaderKeys;
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Connections Catalog");

    worksheet.columns = Object.keys(mappedData[0]).map((key) => ({
      header: key,
      key: key,
      width: 45
    }));

    worksheet.addRows(mappedData);

    const buffer = await workbook.xlsx.writeBuffer();
    this.saveAsExcelFile(buffer, "Grades Catalog");
  }

  public saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    saveAs(data, fileName + '_export_' + EXCEL_EXTENSION);
  }

  public filterGrades(filterName: string) {
    if (!this._grades || this._grades.length === 0) {
      return;
    }

    const activeType = this.normalizeDiscriminator(this.selectedType ?? 'TubularPipe');
    this.selectedType = activeType;
    this.currentFilter = (filterName ?? '').trim();

    this._filteredGrades = filterGradesByTypeAndName(this._grades, activeType, this.currentFilter);

    this.populateFormData(this._filteredGrades);
    this.paginatorFirst.set(0);
    this.gradesCatalogStore.update(state => ({ ...state, selectedType: activeType }));

    if (this.catalogTable) {
      this.catalogTable.first = 0;
    }
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }
}
